import React from 'react';

const PAD = 8;

const viewportSize = () => ({ width: window.innerWidth, height: window.innerHeight });
const scrollOffset = () => ({ x: window.scrollX ?? 0, y: window.scrollY ?? 0 });

// Kendo's Popup is appended to <body> and positioned absolutely, so its `offset` is
// in DOCUMENT coordinates, while a mouse event's clientX/Y is in VIEWPORT
// coordinates. The two differ by the page scroll: on a host page scrolled 4800px a
// right-click at clientY 1125 used to put the menu at document top 1125, i.e. 3.6k
// pixels above the screen. The point is also clamped so the menu stays inside the
// viewport instead of hanging below the fold.
export const clampContextMenuOffset = (point, menuSize, viewport, scroll) => {
  const w = Math.max(0, Number(menuSize?.width) || 0);
  const h = Math.max(0, Number(menuSize?.height) || 0);
  const maxLeft = Math.max(PAD, viewport.width - w - PAD);
  const maxTop = Math.max(PAD, viewport.height - h - PAD);
  const left = Math.min(Math.max(PAD, Number(point?.x) || 0), maxLeft);
  const top = Math.min(Math.max(PAD, Number(point?.y) || 0), maxTop);
  return { left: left + (scroll?.x ?? 0), top: top + (scroll?.y ?? 0) };
};

// Kendo's own collision handling is bypassed: the offset handed to the Popup is
// already final, so it must not be second-guessed.
export const contextMenuPopupProps = {
  collision: { horizontal: 'none', vertical: 'none' },
  popupAlign: { horizontal: 'left', vertical: 'top' },
};

/**
 * State for a right-click template menu rendered in a Kendo Popup:
 *   const { show, offset, menuRef, openAt, close } = useContextMenuPopup();
 *   <Popup show={show} offset={offset} {...contextMenuPopupProps}>
 *     <div ref={menuRef}> ...menu... </div>
 *   </Popup>
 *   onContextMenu={e => { e.preventDefault(); openAt(e); }}
 * The menu's size is only known once it has rendered, so the offset is set from the
 * pointer first and pulled back inside the viewport in a layout effect right after.
 */
export const useContextMenuPopup = () => {
  const [show, setShow] = React.useState(false);
  const [offset, setOffset] = React.useState({ left: 0, top: 0 });
  const pointRef = React.useRef({ x: 0, y: 0 });
  const menuRef = React.useRef(null);

  const openAt = React.useCallback((e) => {
    pointRef.current = { x: e?.clientX ?? 0, y: e?.clientY ?? 0 };
    setOffset(clampContextMenuOffset(pointRef.current, null, viewportSize(), scrollOffset()));
    setShow(true);
  }, []);

  const close = React.useCallback(() => setShow(false), []);

  React.useLayoutEffect(() => {
    if (!show) return;
    const el = menuRef.current;
    if (!el?.getBoundingClientRect) return;
    const rect = el.getBoundingClientRect();
    if (!(rect.width > 0 || rect.height > 0)) return;
    const next = clampContextMenuOffset(
      pointRef.current,
      { width: rect.width, height: rect.height },
      viewportSize(),
      scrollOffset(),
    );
    setOffset((prev) => (prev.left === next.left && prev.top === next.top ? prev : next));
  }, [show]);

  return { show, offset, menuRef, openAt, close };
};
